"use client";

import {
	RiCheckLine,
	RiExpandUpDownLine,
	RiGovernmentLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";

interface Venue {
	id: string;
	slug: string;
	name: string;
	city: string;
	state?: string;
	show_count: number;
}

export interface ShowVenueFilterProps {
	value: string;
	onChange: (value: string) => void;
	availableVenues: Venue[];
	className?: string;
}

export function ShowVenueFilter({
	value,
	onChange,
	availableVenues,
	className,
}: ShowVenueFilterProps) {
	const [open, setOpen] = useState(false);

	const getDisplayName = () => {
		if (value === "all") return "All Venues";
		// Match by id or slug (URL params use slug)
		const venue = availableVenues.find((v) => v.id === value || v.slug === value);
		return venue?.name || "All Venues";
	};

	const handleSelect = useCallback(
		(venueId: string) => {
			const scrollPosition = window.scrollY;
			onChange(venueId === value ? "all" : venueId);
			setOpen(false);
			requestAnimationFrame(() => {
				window.scrollTo(0, scrollPosition);
			});
		},
		[onChange, value],
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn("h-10 w-full justify-between px-3", className)}
				>
					<div className="flex min-w-0 items-center gap-2">
						<RiGovernmentLine className="h-4 w-4 shrink-0" />
						<span className="truncate">{getDisplayName()}</span>
					</div>
					<RiExpandUpDownLine className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[300px] p-0"
				side="bottom"
				align="start"
				sideOffset={4}
				collisionPadding={8}
			>
				<Command>
					<CommandInput placeholder="Search venues..." />
					<CommandList className="max-h-[200px]">
						<CommandEmpty>No venues found.</CommandEmpty>
						<CommandGroup>
							<CommandItem value="all" onSelect={() => handleSelect("all")}>
								<div className="flex items-center justify-between w-full">
									<span className="truncate">All Venues</span>
									<RiCheckLine
										className={cn(
											"ml-2 h-4 w-4 shrink-0",
											value === "all" ? "opacity-100" : "opacity-0",
										)}
									/>
								</div>
							</CommandItem>
							{value !== "all" && (
								<CommandItem
									value="clear-selection"
									onSelect={() => handleSelect("all")}
									className="text-destructive"
								>
									<span>Clear Selection</span>
								</CommandItem>
							)}
							{availableVenues.map((venue) => (
								<CommandItem
									key={venue.id}
									value={`${venue.name} ${venue.city}`}
									onSelect={() => handleSelect(venue.id)}
								>
									<div className="flex items-center justify-between w-full">
										<span className="truncate">{venue.name}</span>
										<RiCheckLine
											className={cn(
												"ml-2 h-4 w-4 shrink-0",
												value === venue.id || value === venue.slug ? "opacity-100" : "opacity-0",
											)}
										/>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
